import React, { useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { FileUp, AlertTriangle } from "lucide-react";
import { ImportPreviewTable, ImportUnmatched } from "./ImportPreviewTable";
import { apiError } from "./balanceFields";

const ColumnSelect = ({ value, onChange, disabled }) => (
  <label className="flex items-center gap-2 text-[12.5px] font-bold text-gray-600">
    Colonna S.I.T.
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} data-testid="import-sit-column"
      className="border border-gray-300 rounded-sm px-2 py-1.5 text-[13px] font-bold text-[#16345c] bg-white outline-none cursor-pointer">
      <option value="residuo">Residuo (già maturato)</option>
      <option value="totale">Residuo totale (con da maturare)</option>
    </select>
  </label>
);

const MappingNote = () => (
  <div className="text-[12px] text-gray-500 bg-gray-50 border border-gray-100 rounded-sm px-3 py-2 leading-relaxed">
    <b>Teleborsa:</b> Ferie → Ferie · Ex festività → Ex festività · Permessi → ROL. <b>S.I.T.:</b> Ferie → Ferie · Permessi (+ ex festività) → ROL · Ex festività azzerate.
    I residui dell'anno precedente vengono azzerati (il prospetto riporta già il residuo complessivo).
  </div>
);

export const BalanceImportDialog = ({ onClose, onApplied }) => {
  const [file, setFile] = useState(null);
  const [column, setColumn] = useState("residuo");
  const [preview, setPreview] = useState(null);
  const [excluded, setExcluded] = useState(new Set());
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const analyze = async (f = file, col = column) => {
    if (!f) return toast.error("Seleziona il PDF del prospetto");
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      form.append("sit_column", col);
      const res = await api.post("/balances/import/preview", form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data); setExcluded(new Set()); setConfirming(false);
      setReason(`Import prospetto residui ${res.data.company_label}${res.data.period ? ` ${res.data.period}` : ""}`);
    } catch (e) { toast.error(apiError(e, "Errore nella lettura del PDF")); }
    finally { setBusy(false); }
  };

  const changeColumn = (col) => { setColumn(col); if (preview) analyze(file, col); };
  const toggle = (id) => setExcluded((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const matched = (preview?.rows || []).filter((r) => r.employee_id);
  const unmatched = (preview?.rows || []).filter((r) => !r.employee_id);
  const toApply = matched.filter((r) => !excluded.has(r.employee_id) && r.changes.length > 0);

  const apply = async () => {
    if (reason.trim().length < 3) return toast.error("La motivazione è obbligatoria");
    setBusy(true);
    try {
      const res = await api.post("/balances/import/apply", { import_id: preview.import_id, exclude_ids: [...excluded], reason: reason.trim() });
      toast.success(`Saldi aggiornati per ${res.data.updated} dipendenti (${res.data.changes} modifiche registrate)`);
      onApplied();
    } catch (e) { toast.error(apiError(e, "Errore nell'applicazione dell'import")); setConfirming(false); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-[920px] max-h-[92vh] overflow-y-auto" data-testid="balance-import-dialog">
        <DialogHeader>
          <DialogTitle>Importa prospetto residui (PDF)</DialogTitle>
          <DialogDescription className="text-[13px]">Prospetto paghe "Situazione ferie e permessi (solo residui)" — Teleborsa o S.I.T.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 border border-dashed border-[#16345c]/40 rounded-sm px-3 py-2 text-[13px] font-bold text-[#16345c] cursor-pointer hover:bg-[#16345c]/5 transition-colors">
              <FileUp size={15} /> {file ? file.name : "Scegli PDF"}
              <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="import-file-input"
                onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); setPreview(null); }} />
            </label>
            <ColumnSelect value={column} onChange={changeColumn} disabled={busy} />
            <button onClick={() => analyze()} disabled={busy || !file} data-testid="import-analyze"
              className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors disabled:opacity-50">{busy && !preview ? "Analisi..." : "Analizza"}</button>
          </div>
          <MappingNote />
          {preview && (
            <>
              <div className="text-[13px] text-gray-700" data-testid="import-summary">
                <b className="text-[#16345c]">{preview.company_label}</b>{preview.period ? ` · periodo ${preview.period}` : ""} · {preview.matched} abbinati · {preview.unmatched} non trovati · <b>{toApply.length}</b> da aggiornare
              </div>
              <ImportPreviewTable rows={matched} excluded={excluded} onToggle={toggle} />
              <ImportUnmatched rows={unmatched} />
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1">Motivazione <span className="text-[#e30613]">*</span></label>
                <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} data-testid="import-reason"
                  className="w-full border border-gray-300 rounded-sm px-3 py-2 text-[13.5px] outline-none focus:border-[#16345c]" />
              </div>
              {confirming && (
                <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-[12.5px] px-3 py-2 rounded-sm" data-testid="import-confirm-warning">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" /> Confermi l'aggiornamento dei saldi di {toApply.length} dipendenti? Ogni modifica verrà registrata nello storico.
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter>
          <button onClick={confirming ? () => setConfirming(false) : onClose} disabled={busy} data-testid="import-cancel"
            className="px-4 py-2 text-[13px] font-semibold text-gray-600 hover:bg-gray-100 rounded-sm transition-colors">{confirming ? "Indietro" : "Annulla"}</button>
          {preview && (confirming
            ? <button onClick={apply} disabled={busy} data-testid="import-apply-confirm" className="px-4 py-2 text-[13px] font-bold text-white bg-[#009640] hover:bg-[#007a34] rounded-sm transition-colors disabled:opacity-60">{busy ? "Applicazione..." : "Conferma import"}</button>
            : <button onClick={() => setConfirming(true)} disabled={busy || toApply.length === 0} data-testid="import-apply" className="px-4 py-2 text-[13px] font-bold text-white bg-[#16345c] hover:bg-[#0d1b2f] rounded-sm transition-colors disabled:opacity-50">Applica {toApply.length} aggiornamenti</button>)}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
